import React, { lazy } from 'react';
import { createBrowserRouter } from 'react-router';
import { Root } from './Root';
import { ProtectedRoute } from './components/mtt/ProtectedRoute';
// Landing screen stays eager so the very first paint has no lazy-load flash.
import { WelcomeScreen } from './screens/WelcomeScreen';

// Every other screen is code-split: its JS only ships to the browser when its route is
// first visited, keeping the initial bundle small. After the first paint, prefetchRoutes()
// warms every chunk in the background so later navigations never show a loading spinner.
const loaders = {
  SignUpScreen: () => import('./screens/SignUpScreen'),
  SignInScreen: () => import('./screens/SignInScreen'),
  VerifyScreen: () => import('./screens/VerifyScreen'),
  VerifiedScreen: () => import('./screens/VerifiedScreen'),
  ProfileSetupScreen: () => import('./screens/ProfileSetupScreen'),
  FamilySearchScreen: () => import('./screens/FamilySearchScreen'),
  DashboardScreen: () => import('./screens/DashboardScreen'),
  PickMomentScreen: () => import('./screens/PickMomentScreen'),
  StoryPromptsScreen: () => import('./screens/StoryPromptsScreen'),
  StoryBuilderScreen: () => import('./screens/StoryBuilderScreen'),
  StoryOrganizerScreen: () => import('./screens/StoryOrganizerScreen'),
  MemoryBookScreen: () => import('./screens/MemoryBookScreen'),
  StoryPreviewScreen: () => import('./screens/StoryPreviewScreen'),
  ShareSettingsScreen: () => import('./screens/ShareSettingsScreen'),
  ConfirmationScreen: () => import('./screens/ConfirmationScreen'),
  NetworkScreen: () => import('./screens/NetworkScreen'),
  MyProfileScreen: () => import('./screens/MyProfileScreen'),
  VideoEditorScreen: () => import('./screens/VideoEditorScreen'),
  EditStoryScreen: () => import('./screens/EditStoryScreen'),
  SubscriptionScreen: () => import('./screens/SubscriptionScreen'),
  UploadSuccessScreen: () => import('./screens/UploadSuccessScreen'),
  DesignSystemScreen: () => import('./screens/DesignSystemScreen'),
  SettingsScreen: () => import('./screens/SettingsScreen'),
  StoryMerchandiseScreen: () => import('./screens/StoryMerchandiseScreen'),
  MerchandiseOrderSuccessScreen: () => import('./screens/MerchandiseOrderSuccessScreen'),
} as const;

type ScreenName = keyof typeof loaders;

// Screens use named exports, so each import is adapted to the default-export shape React.lazy expects.
const screen = <K extends ScreenName>(name: K) =>
  lazy(() => loaders[name]().then(m => ({ default: (m as Record<string, React.ComponentType>)[name] })));

const SignUpScreen = screen('SignUpScreen');
const SignInScreen = screen('SignInScreen');
const VerifyScreen = screen('VerifyScreen');
const VerifiedScreen = screen('VerifiedScreen');
const ProfileSetupScreen = screen('ProfileSetupScreen');
const FamilySearchScreen = screen('FamilySearchScreen');
const DashboardScreen = screen('DashboardScreen');
const PickMomentScreen = screen('PickMomentScreen');
const StoryPromptsScreen = screen('StoryPromptsScreen');
const StoryBuilderScreen = screen('StoryBuilderScreen');
const StoryOrganizerScreen = screen('StoryOrganizerScreen');
const MemoryBookScreen = screen('MemoryBookScreen');
const StoryPreviewScreen = screen('StoryPreviewScreen');
const ShareSettingsScreen = screen('ShareSettingsScreen');
const ConfirmationScreen = screen('ConfirmationScreen');
const NetworkScreen = screen('NetworkScreen');
const MyProfileScreen = screen('MyProfileScreen');
const VideoEditorScreen = screen('VideoEditorScreen');
const EditStoryScreen = screen('EditStoryScreen');
const SubscriptionScreen = screen('SubscriptionScreen');
const UploadSuccessScreen = screen('UploadSuccessScreen');
const DesignSystemScreen = screen('DesignSystemScreen');
const SettingsScreen = screen('SettingsScreen');
const StoryMerchandiseScreen = screen('StoryMerchandiseScreen');
const MerchandiseOrderSuccessScreen = screen('MerchandiseOrderSuccessScreen');

/** Download every screen chunk while the browser is idle. Safe to call repeatedly. */
let prefetched = false;
export function prefetchRoutes() {
  if (prefetched) return;
  prefetched = true;
  Object.values(loaders).forEach(load => load().catch(() => { prefetched = false; }));
}

export const router = createBrowserRouter([
  {
    path: '/',
    Component: Root,
    children: [
      // Public — auth flow
      { index: true, Component: WelcomeScreen },
      { path: 'signup', Component: SignUpScreen },
      { path: 'signin', Component: SignInScreen },
      { path: 'verify', Component: VerifyScreen },
      { path: 'verified', Component: VerifiedScreen },
      { path: 'profile', Component: ProfileSetupScreen },
      { path: 'connect', Component: FamilySearchScreen },

      // Protected — requires active session
      {
        Component: ProtectedRoute,
        children: [
          { path: 'home', Component: DashboardScreen },
          { path: 'create/pick', Component: PickMomentScreen },
          { path: 'create/prompts', Component: StoryPromptsScreen },
          { path: 'create/story', Component: StoryBuilderScreen },
          { path: 'create/organize', Component: StoryOrganizerScreen },
          { path: 'create/book', Component: MemoryBookScreen },
          { path: 'create/preview', Component: StoryPreviewScreen },
          { path: 'create/share', Component: ShareSettingsScreen },
          { path: 'create/done', Component: ConfirmationScreen },
          { path: 'network', Component: NetworkScreen },
          { path: 'my-profile', Component: MyProfileScreen },
          // { path: 'edit/video', Component: VideoEditorScreen },
          { path: 'edit/story', Component: EditStoryScreen },
          { path: 'edit/success', Component: UploadSuccessScreen },
          { path: 'subscribe', Component: SubscriptionScreen },
          { path: 'design', Component: DesignSystemScreen },
          { path: 'settings', Component: SettingsScreen },
          { path: 'shop', Component: StoryMerchandiseScreen },
          { path: 'merchandise/success', Component: MerchandiseOrderSuccessScreen },
        ],
      },
    ],
  },
]);
